import { useEffect, useMemo } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';

import { AspirationsToday } from '@/rtk-query.gql';
import { useDonorScheduleData } from '@/services/aspiration/hooks';
import {
  useFertDataQuery,
  useUpdateAspirationOpuSearch,
} from '@/services/opu-search/hooks';
import {
  GET_ASPIRATION_BY_DONOR_AND_ID,
  GET_ASPIRATION_BY_DONOR_AND_IDQuery,
  GET_ASPIRATION_BY_DONOR_AND_IDQueryVariables,
  STRING_MAP_SEARCH,
  STRING_MAP_SEARCHQuery,
  STRING_MAP_SEARCHQueryVariables,
} from '@/types.gql';

type CytoplasmOption = { label: string; value: number };

export const useAspirationFormResources = ({
  selectedAspiration,
  opuDate,
  opuSiteId,
}: {
  selectedAspiration: AspirationsToday | undefined | null;
  opuDate: string;
  opuSiteId: string | null;
}) => {
  const [loadAsp, aspResult] = useLazyQuery<
    GET_ASPIRATION_BY_DONOR_AND_IDQuery,
    GET_ASPIRATION_BY_DONOR_AND_IDQueryVariables
  >(GET_ASPIRATION_BY_DONOR_AND_ID, { fetchPolicy: 'network-only' });

  const { mutateAsync: updateAspirationRest, loading } =
    useUpdateAspirationOpuSearch();

  const { data: embFertData } = useFertDataQuery({
    donorId: selectedAspiration?.donorId ?? null,
    ranch: opuSiteId,
    enabled: !!selectedAspiration,
  });

  const { data: donorScheduleData } = useDonorScheduleData({
    donorId: selectedAspiration?.donorId,
    aspirationDate: opuDate,
    ranch: opuSiteId,
  });

  const { data: cytoData } = useQuery<
    STRING_MAP_SEARCHQuery,
    STRING_MAP_SEARCHQueryVariables
  >(STRING_MAP_SEARCH, {
    variables: { objectTypeCode: 10006, name: 'tog_ivmcyto' },
  });

  const cytoplasmQualityData: CytoplasmOption[] = useMemo(
    () =>
      cytoData?.stringMap
        ?.filter((i) => i.value !== '.')
        .map((i) => ({ label: i.value, value: i.key })) ?? [],
    [cytoData],
  );

  useEffect(() => {
    if (selectedAspiration) {
      loadAsp({
        variables: {
          donorId: selectedAspiration.donorId ?? '',
          aspirationId: selectedAspiration.aspirationId ?? '',
        },
      });
    }
  }, [loadAsp, selectedAspiration]);

  const aspirationForm = aspResult.data?.aspirations?.items?.[0];

  const aspirationData = useMemo(() => {
    if (!selectedAspiration) return [];
    const a = selectedAspiration;
    return [
      { category: 'Large:', left: a.ll, right: a.rl },
      { category: 'Medium:', left: a.lm, right: a.rm },
      { category: 'Small:', left: a.ls, right: a.rs },
      { category: 'Totals:', left: a.totalLeft, right: a.totalRight },
      { category: "CL's:", left: a.cll ?? 0, right: a.clr ?? 0 },
      { category: 'Cysts:', left: a.cystl ?? 0, right: a.cystr ?? 0 },
    ];
  }, [selectedAspiration]);

  const saveAspiration = (params: {
    input: {
      aspirationId: string;
      indexIvmTech?: string | null;
      siteId: string;
      aspirationDate: string;
      fertPlan?: string | null;
      ivmMat?: number | null;
      gradeAbc?: number | null;
      gradeD?: number | null;
      gradeE?: number | null;
      ivmTechTime: string;
      ivmRec?: number | null;
      ivmComment?: string | null;
      ivmCyto?: string | null;
      usdaSeal?: string | null;
      enim?: number | null;
      dishType?: string | null;
      ivmTechId?: string | null;
    };
    isReturn: string;
  }) => updateAspirationRest(params);

  return {
    aspirationForm,
    aspirationData,
    embFertDataByDonorId: embFertData,
    donorScheduleData,
    cytoplasmQualityData,
    saveAspiration,
    loading,
    initialLoading: aspResult.loading,
  };
};
